import {
    Headline,
    Spacer,
    StyleFunction,
    Text,
    useThemedStyle,
} from "@venuepos/react-common";
import { GQShiftQuery } from "graphql-sdk";
import { formatAmount, formatDateTime, SupportedCurrenciesType } from "lib";
import { AvailableLocale } from "locales";
import { useTranslation } from "react-i18next";
import React, { View } from "react-native";

export function ShiftBalance({
    data,
    locale,
    currency,
}: {
    data?: GQShiftQuery;
    locale: AvailableLocale;
    currency: SupportedCurrenciesType;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);

    return (
        <View style={styles.contentItem}>
            <Headline size="h5" style={styles.headline}>
                {t("common.balance", "Balance")}
            </Headline>
            {data.shift.payments
                ?.filter(itr => itr.type === "CASH" && itr.event === "OPEN")
                .map((payment, index) => (
                    <View key={`balance_row_${index}`}>
                        <View style={styles.items}>
                            <Text style={styles.itemLeft}>
                                {t(
                                    "backoffice.shift.cash_at_open",
                                    "{{type}} at open",
                                    {
                                        type: t(
                                            "paymenttypes." +
                                                payment.name.toLocaleUpperCase(),
                                            payment.name
                                        ),
                                    }
                                )}
                            </Text>
                            <Text style={styles.itemRight}>
                                {formatAmount(
                                    payment.amount / payment.currencyRate,
                                    payment.currency as SupportedCurrenciesType,
                                    {
                                        printCurrency: true,
                                    }
                                )}
                            </Text>
                        </View>
                        {data.shift.funds
                            ?.filter(itr => itr.name === payment.name)
                            .map((item, i) => (
                                <View style={styles.items} key={`funds_${i}`}>
                                    <Text style={styles.itemLeft}>
                                        {item.type === "IN"
                                            ? t(
                                                  "backoffice.shift.cash_in",
                                                  "{{type}} in ({{amount}})",
                                                  {
                                                      amount: formatDateTime(
                                                          item.createdAt,
                                                          locale
                                                      ),
                                                      type: t(
                                                          "paymenttypes." +
                                                              payment.name.toLocaleUpperCase(),
                                                          payment.name
                                                      ),
                                                      interpolation: {
                                                          escapeValue: false,
                                                      },
                                                  }
                                              )
                                            : t(
                                                  "backoffice.shift.cash_out",
                                                  "{{type}} out ({{amount}})",
                                                  {
                                                      amount: formatDateTime(
                                                          item.createdAt,
                                                          locale
                                                      ),
                                                      type: t(
                                                          "paymenttypes." +
                                                              payment.name.toLocaleUpperCase(),
                                                          payment.name
                                                      ),
                                                      interpolation: {
                                                          escapeValue: false,
                                                      },
                                                  }
                                              )}
                                    </Text>
                                    <Text style={styles.itemRight}>
                                        {formatAmount(
                                            (item.amount /
                                                payment.currencyRate) *
                                                (item.type === "IN" ? 1 : -1),
                                            payment.currency as SupportedCurrenciesType,
                                            {
                                                printCurrency: true,
                                            }
                                        )}
                                    </Text>
                                </View>
                            ))}
                        <View style={styles.items}>
                            <Text style={styles.itemLeft}>
                                {t("backoffice.shift.sale", "{{type}} sale", {
                                    type: t(
                                        "paymenttypes." +
                                            payment.name.toLocaleUpperCase(),
                                        payment.name
                                    ),
                                })}
                            </Text>
                            <Text style={styles.itemRight}>
                                {formatAmount(
                                    (data.shift.cashInfoPerMethod.cashSalePerMethod?.find(
                                        itr =>
                                            itr.paymentMethodId ===
                                            payment.paymentMethodId
                                    )?.amount || 0) / payment.currencyRate,
                                    payment.currency as SupportedCurrenciesType,
                                    {
                                        printCurrency: true,
                                    }
                                )}
                            </Text>
                        </View>
                        <View style={styles.items}>
                            <Text style={styles.itemLeft}>
                                {t(
                                    "backoffice.shift.method_difference",
                                    "{{type}} difference",
                                    {
                                        type: t(
                                            "paymenttypes." +
                                                payment.name.toLocaleUpperCase(),
                                            payment.name
                                        ),
                                    }
                                )}
                            </Text>
                            <Text style={styles.itemRight}>
                                {formatAmount(
                                    (data.shift.cashInfoPerMethod.cashDifferencePerMethod?.find(
                                        itr =>
                                            itr.paymentMethodId ===
                                            payment.paymentMethodId
                                    )?.amount * -1 || 0) / payment.currencyRate,
                                    payment.currency as SupportedCurrenciesType,
                                    {
                                        printCurrency: true,
                                    }
                                )}
                            </Text>
                        </View>
                        <View style={[styles.items, styles.sum]}>
                            <Text style={[styles.itemLeft, styles.sumText]}>
                                {t(
                                    "backoffice.shift.cash_at_close",
                                    "{{type}} at close",
                                    {
                                        type: t(
                                            "paymenttypes." +
                                                payment.name.toLocaleUpperCase(),
                                            payment.name
                                        ),
                                    }
                                )}
                            </Text>
                            <Text style={[styles.itemRight, styles.sumText]}>
                                {data.shift.closedAt
                                    ? formatAmount(
                                          (data.shift.payments?.find(
                                              itr =>
                                                  itr.type === "CASH" &&
                                                  itr.event === "CLOSE" &&
                                                  itr.paymentMethodId ===
                                                      payment.paymentMethodId
                                          )?.amount || 0) /
                                              payment.currencyRate,
                                          payment.currency as SupportedCurrenciesType,
                                          {
                                              printCurrency: true,
                                          }
                                      )
                                    : "-"}
                            </Text>
                        </View>

                        <Spacer />
                    </View>
                ))}

            <View style={styles.items}>
                <Text style={[styles.itemLeft, styles.sumText]}>
                    {t("backoffice.shift.difference", "Difference")}
                </Text>
                <Text style={[styles.itemRight, styles.sumText]}>
                    {data.shift.closedAt
                        ? formatAmount(
                              data.shift.cashDifference === 0
                                  ? 0
                                  : data.shift.cashDifference * -1,
                              currency,
                              {
                                  printCurrency: true,
                              }
                          )
                        : "-"}
                </Text>
            </View>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    contentItem: {
        flex: 1,
    },
    items: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    itemLeft: { flex: 3 },
    headline: {
        marginBottom: 0,
    },

    sum: {
        borderTopWidth: 1,
        borderTopColor: theme.colors.grey500,
    },

    sumText: {
        fontWeight: "bold",
    },
});
